import {Plural, Trans} from '@lingui/macro';
import React, {Fragment, ReactNode} from 'react';

import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {MessageWarning, MessageWarningTypesEnum} from '../../../src/helpers/models/messageHelpers';

export interface LocalizedMessageWarning {
  title: ReactNode;
  text: ReactNode;
}

export function buildLocalizedMessageWarning(
  warning: MessageWarning,
  renderEmphasisText: (content: ReactNode) => ReactNode,
): LocalizedMessageWarning {
  switch (warning.type) {
    case MessageWarningTypesEnum.NO_SUBJECT:
      return {
        title: <Trans id="warning-nosubject-title">Missing subject</Trans>,
        text: <Trans id="warning-nosubject-content">This message has no subject.</Trans>,
      };

    case MessageWarningTypesEnum.NO_ATTACHMENTS:
      return {
        title: <Trans id="warning-noattachments-title">Attachment reminder</Trans>,
        text: (
          <Trans id="warning-noattachments-content">
            You may have forgotten to attach a file to this message.
          </Trans>
        ),
      };

    case MessageWarningTypesEnum.FAILED_ATTACHMENTS:
      return {
        title: <Trans id="warning-failedattachments-title">Attachment reminder</Trans>,
        text: (
          <Trans id="warning-failedattachments-content">
            This message has attachments that failed to upload.
          </Trans>
        ),
      };

    case MessageWarningTypesEnum.UPLOADING_ATTACHMENTS:
      return {
        title: <Trans id="warning-uploadingattachments-title">Attachment reminder</Trans>,
        text: (
          <Trans id="warning-uploadingattachments-content">
            This message has attachments that are currently uploading.
          </Trans>
        ),
      };

    case MessageWarningTypesEnum.MANY_RECIPIENTS: {
      const {recipientCount, domainNames} = warning;
      return {
        title: <Trans id="warning-manyrecipients-title">Confirmation</Trans>,
        text: (
          <Trans id="warning-manyrecipients-content">
            This message will be sent to {renderEmphasisText(recipientCount)} recipients at{' '}
            {renderDomainNames(domainNames, renderEmphasisText)}.
          </Trans>
        ),
      };
    }

    case MessageWarningTypesEnum.HAS_RAW_VARIABLES: {
      const {rawVariables} = warning;
      return {
        title: <Trans id="warning-hasrawvariables-title">Unfilled variables</Trans>,
        text:
          rawVariables.length < 10 ? (
            <Plural
              id="warning-hasrawvariables-content"
              value={rawVariables.length}
              one={`You have # unfilled variable: ${rawVariables[0]}.`}
              other={`You have # unfilled variables: ${rawVariables.join(', ')}.`}
            />
          ) : (
            <Trans id="warning-hasrawvariables-over10-content">
              You have unfilled variables in the message.
            </Trans>
          ),
      };
    }

    default:
      throw new UnreachableError(warning);
  }
}

function renderDomainNames(
  domainNames: readonly string[],
  renderEmphasisText: (content: ReactNode) => ReactNode,
) {
  const fullDomainNames = domainNames.slice(0, 5);
  const fullDomainNamesRender = fullDomainNames.map((d, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Fragment key={index}>
      {index > 0 && ', '}
      {renderEmphasisText(d)}
    </Fragment>
  ));

  // Show a counter for the rest.
  const extraDomainNamesCount = domainNames.length - fullDomainNames.length;
  const extraDomainsSummary = extraDomainNamesCount > 0 ? ` (+${extraDomainNamesCount})` : '';

  return (
    <>
      {fullDomainNamesRender}
      {extraDomainsSummary}
    </>
  );
}
